import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { CdkVirtualScrollViewport, ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  ElementRef,
  forwardRef,
  inject,
  Injector,
  input,
  OnDestroy,
  output,
  signal,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import { LoggerProvider } from '@shared';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';
import { NeoAutocompleteV2Module } from '@neogov/pattern-library/neo-autocomplete-v2';
import { NeoChipsModule } from '@neogov/pattern-library/neo-chips';
import { NeoFormFieldModule } from '@neogov/pattern-library/neo-form-field';
import { ButtonComponent } from '../button/button.component';
import { IconComponent } from '../icon/icon.component';
import { FieldErrorsComponent } from '../field-errors/field-errors.component';

/**
 * Type-safe autocomplete selection event structure
 * Represents the event emitted by neo-autocomplete when an option is selected
 */
export interface AutocompleteSelectionEvent<T> {
  /**
   * The selected option wrapper (the underlying NeoOption instance)
   */
  option: {
    /**
     * The actual item value that was selected
     */
    value: T;
    /**
     * NeoOption method to reset its internal selected state. In multiple mode,
     * NeoOption toggles an internal `_selected` flag on every click, and the
     * autocomplete suppresses events where the flag is `false`. Calling
     * `deselect()` keeps the flag clean so repeated clicks on the same option
     * (e.g. a sentinel item that stays in the list) continue to fire events.
     */
    deselect?: () => void;
  };
}

/**
 * Group structure for organizing items into categories
 * Used when items should be displayed in grouped sections in the dropdown
 */
export interface MultiselectGroup<T> {
  /**
   * Display label for the group
   * @example "Active Users", "Pending Invites"
   */
  label: string;
  /**
   * Items belonging to this group
   */
  items: T[];
}

/**
 * Pagination configuration for paginated mode
 * When provided, enables server-side pagination with virtual scrolling
 */
export interface PaginationConfig {
  /**
   * Number of items per page/request
   * @example 50
   */
  pageSize: number;

  /**
   * Total number of available items on server
   * Used to determine if more data exists
   */
  totalCount: number;

  /**
   * Whether to enable CDK virtual scrolling
   * Improves performance for large lists
   * @default true
   */
  enableVirtualScroll: boolean;

  /**
   * Fixed height per item in pixels (required for virtual scroll)
   * Must account for option height + subtext if present
   * @example 48 (standard option), 64 (with subtext)
   */
  itemHeight: number;

  /**
   * Whether to enable search/filter functionality
   * When false: searchChange still emits (for client-side hints), but enableSearch=false in PaginationParams
   * When true: searchChange emits and enableSearch=true in PaginationParams (parent should send Filter parameter)
   * @default false
   */
  enableSearch?: boolean;
}

/**
 * Parameters emitted when more data is needed
 * Follows Kendo-style skip/take pattern
 */
export interface PaginationParams {
  /**
   * Number of items to skip (offset)
   * @example 0, 50, 100
   */
  skip: number;

  /**
   * Number of items to fetch
   * @example 50
   */
  take: number;

  /**
   * Current search term for server-side filtering
   * @example "john", "admin"
   */
  search: string;

  /**
   * Whether search/filter is enabled for this request
   * Parent should check this flag before building Filter parameter
   * Note: search term is always provided, but parent only sends Filter when enableSearch=true
   * @example true, false
   */
  enableSearch?: boolean;
}

export interface OptionViewModel<T> {
  item: T;
  trackKey: string | number;
  displayText: string;
  subtext: string | null;
  isVisuallySelected: boolean;
  isIndeterminate: boolean;
  isDisabled: boolean;
  disabledReason: string | null;
}

export interface GroupOptionViewModel<T> {
  label: string;
  itemVms: OptionViewModel<T>[];
}

/**
 * Multiselect Chip Field Component
 *
 * A reusable presentational component for selecting multiple items from a searchable
 * dropdown with chip visualization. Commonly used for assigning users, members, or
 * other entities to groups, roles, or teams.
 *
 * This component implements ControlValueAccessor for the search input only, allowing
 * parent components to control filtering logic. Selection state is managed by the
 * parent through inputs and outputs.
 *
 * @example Basic usage (flat list)
 * ```html
 * <cm-multiselect-chip-field
 *   label="Add members"
 *   buttonLabel="Add members"
 *   [items]="availableItems()"
 *   [selectedItems]="selectedItems()"
 *   [displayFn]="item => item.name"
 *   [subtextFn]="item => item.email"
 *   [trackByFn]="item => item.id"
 *   autId="team-members"
 *   (itemSelected)="onItemSelected($event)"
 *   (itemRemoved)="onItemRemoved($event)"
 *   (allItemsRemoved)="onClearAll()"
 *   (buttonClick)="onAddClick()"
 * />
 * ```
 *
 * @example With grouped options
 * ```html
 * <cm-multiselect-chip-field
 *   label="Select case types"
 *   buttonLabel="Apply"
 *   [groups]="caseTypeGroups()"
 *   [selectedItems]="selectedCaseTypes()"
 *   [displayFn]="item => item.name"
 *   [trackByFn]="item => item.id"
 *   autId="case-types"
 *   (itemSelected)="onItemSelected($event)"
 *   (itemRemoved)="onItemRemoved($event)"
 * />
 *
 * // caseTypeGroups() returns: MultiselectGroup<T>[]
 * // [{ label: 'Misconduct & Ethics', items: [...] }, { label: 'Conflict & Performance', items: [...] }]
 * ```
 *
 * @template T - The type of items being selected
 */
@Component({
  selector: 'cm-multiselect-chip-field',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ScrollingModule,
    NeoFormFieldModule,
    NeoChipsModule,
    NeoAutocompleteV2Module,
    ButtonComponent,
    IconComponent,
    FieldErrorsComponent,
  ],
  templateUrl: './multiselect-chip-field.component.html',
  styleUrl: './multiselect-chip-field.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultiselectChipFieldComponent),
      multi: true,
    },
  ],
})
export class MultiselectChipFieldComponent<T> implements ControlValueAccessor, OnDestroy {
  // --- Constants ---

  /**
   * Default placeholder text when no items are selected
   */
  private static readonly DEFAULT_PLACEHOLDER = 'Search and select...';

  /**
   * Default button loading text
   */
  private static readonly DEFAULT_BUTTON_LOADING_TEXT = 'Adding...';

  /**
   * Default button icon (Material Icons name)
   */
  private static readonly DEFAULT_BUTTON_ICON = 'person_add';

  /**
   * Default button color variant
   */
  private static readonly DEFAULT_BUTTON_COLOR: 'primary' | 'success' | 'danger' | 'gray' = 'primary';

  /**
   * Default keyboard separator keys for chip input
   */
  private static readonly DEFAULT_SEPARATOR_KEYS = [ENTER, COMMA];

  // --- Labels & Text ---

  /**
   * Label displayed above the form field
   * @example "Add members", "Add users"
   */
  public readonly label = input.required<string>();

  /**
   * When true the visible `neo-label` is not rendered, but the label text is
   * still used as the accessible name of the chip grid and search input.
   * Use for fields that must not show a visible label but still need a name (a11y).
   * @default false
   */
  public readonly hideLabel = input<boolean>(false);

  /**
   * Placeholder text shown when no items are selected
   * @default "Search and select..."
   */
  public readonly placeholder = input<string>(MultiselectChipFieldComponent.DEFAULT_PLACEHOLDER);

  /**
   * Hint text displayed below the form field
   * @example "Select one or more items"
   */
  public readonly hint = input<string>('');

  /**
   * Text displayed on the action button
   * Required only when showButton is true
   * @example "Add members", "Add users"
   */
  public readonly buttonLabel = input<string>('');

  /**
   * Text displayed on button during loading state
   * @default "Adding..."
   */
  public readonly buttonLoadingText = input<string>(MultiselectChipFieldComponent.DEFAULT_BUTTON_LOADING_TEXT);

  // --- Button Config ---

  /**
   * Whether to show the action button
   * When false, selections emit immediately without needing a button click
   * @default true
   */
  public readonly showButton = input<boolean>(true);

  /**
   * Material icon name for the button
   * @default "person_add"
   */
  public readonly buttonIcon = input<string>(MultiselectChipFieldComponent.DEFAULT_BUTTON_ICON);

  /**
   * Whether the action button is disabled
   * @default false
   */
  public readonly buttonDisabled = input<boolean>(false);

  /**
   * Tooltip shown on the action button. Surfaces even while the button is disabled
   * (e.g. to explain why it can't be clicked yet).
   * @default ""
   */
  public readonly buttonTooltip = input<string>('');

  /**
   * Whether the button is in loading state
   * @default false
   */
  public readonly buttonLoading = input<boolean>(false);

  /**
   * Button color variant
   * @default "primary"
   */
  public readonly buttonColor = input<'primary' | 'success' | 'danger' | 'gray'>(MultiselectChipFieldComponent.DEFAULT_BUTTON_COLOR);

  /**
   * Whether to always show individual chips instead of collapsing to count
   * When true, shows each selected item as a separate chip
   * When false, shows "X selected" for multiple selections
   * @default false
   */
  public readonly alwaysShowIndividualChips = input<boolean>(false);

  // --- Data ---
  // Note: Provide EITHER items OR groups, not both

  /**
   * Available items to select from (flat list)
   * Parent should filter this list to exclude already selected items.
   * The dropdown will only show items in this list.
   *
   * Note: Use either this OR groups, not both.
   * If groups is provided, items will be ignored.
   */
  public readonly items = input<T[]>([]);

  /**
   * Grouped items to select from
   * Parent should filter items in groups to exclude already selected items.
   *
   * Note: Use either this OR items, not both.
   * If provided, takes precedence over items.
   */
  public readonly groups = input<MultiselectGroup<T>[]>([]);

  // --- Pagination Inputs ---

  /**
   * Pagination configuration for paginated mode
   * When provided (non-null), enables server-side pagination with virtual scrolling
   * When null/undefined, component operates in static mode (backward compatible)
   * @default undefined
   */
  public readonly paginationConfig = input<PaginationConfig | null>();

  /**
   * Items loaded from server in paginated mode
   * Parent accumulates items across pages and passes full array here
   * Only used when paginationConfig is provided
   * @default []
   */
  public readonly loadedItems = input<T[]>([]);

  /**
   * Whether more items are being loaded (pagination in progress)
   * Shows loading indicator at bottom of dropdown
   * @default false
   */
  public readonly loadingMore = input<boolean>(false);

  /**
   * Whether more items are available on server
   * Calculated by parent: loadedItems.length < totalCount
   * Only relevant in paginated mode
   * @default true
   */
  public readonly hasMore = input<boolean>(true);

  /**
   * Currently selected items
   * Managed by parent component
   */
  public readonly selectedItems = input.required<T[]>();

  // --- Display Functions ---

  /**
   * Function to get display text for an item
   * @example (item) => item.name
   * @example (item) => `${item.firstName} ${item.lastName}`
   */
  public readonly displayFn = input.required<(item: T) => string>();

  /**
   * Optional function to get subtext for an item in dropdown
   * @example (item) => item.email
   * @example (item) => `${item.email} - ${item.role}`
   */
  public readonly subtextFn = input<((item: T) => string) | null>(null);

  /**
   * Function to get unique identifier for tracking items
   * @example (item) => item.id
   * @example (item) => item.userId
   */
  public readonly trackByFn = input.required<(item: T) => string | number>();

  /**
   * Optional function to determine if an item should be disabled
   * Disabled items appear in the dropdown but cannot be selected
   * @example (item) => item.isAlreadyUsed
   */
  public readonly disabledFn = input<((item: T) => boolean) | null>(null);

  /**
   * Optional function to override an option's visual selection state.
   * Returning `'indeterminate'` renders a minus glyph in place of the
   * checkmark — useful for parent-driven "select all" rows that aggregate
   * over the real selection. When omitted, the visual state follows
   * `selectedItems` directly.
   * @example (item) => isSelectAll(item) ? someSelected() ? 'indeterminate' : 'unchecked' : 'unchecked'
   */
  public readonly getOptionState = input<
    ((item: T) => 'checked' | 'unchecked' | 'indeterminate') | null
  >(null);

  /**
   * Optional function to get disabled reason text for tooltips
   * @example (item) => 'Already used in another rule'
   */
  public readonly disabledReasonFn = input<((item: T) => string | null) | null>(null);

  // --- Form Config ---

  /**
   * Keyboard separator keys for chip input
   * @default [ENTER, COMMA]
   */
  public readonly separatorKeys = input<number[]>(MultiselectChipFieldComponent.DEFAULT_SEPARATOR_KEYS);

  /**
   * Automation test ID prefix
   * Used to generate unique IDs for testing
   */
  public readonly autId = input.required<string>();

  // --- Error Handling ---

  /**
   * Custom error messages for specific validators
   * @example { required: 'Please select at least one member' }
   */
  public readonly errorMessages = input<Partial<Record<string, string>>>({});

  /**
   * When to show error messages
   * @default 'touched'
   */
  public readonly showErrorsWhen = input<'touched' | 'dirty' | 'always'>('touched');

  /**
   * Whether to hide error messages
   * @default false
   */
  public readonly hideErrors = input<boolean>(false);

  // --- Events ---

  /**
   * Emitted when user selects an item from the dropdown
   */
  public readonly itemSelected = output<T>();

  /**
   * Emitted when user removes a single chip
   */
  public readonly itemRemoved = output<T>();

  /**
   * Emitted when user clears all selected chips
   */
  public readonly allItemsRemoved = output<void>();

  /**
   * Emitted when user clicks the action button
   */
  public readonly buttonClick = output<void>();

  // --- Pagination Events ---

  /**
   * Emitted when more data is needed (infinite scroll)
   * Parent should fetch next page and append to loadedItems
   * Only emitted in paginated mode
   */
  public readonly loadMore = output<PaginationParams>();

  /**
   * Emitted when search term changes (for server-side filtering).
   * Emitted in both static and paginated modes with 300ms debounce.
   * In paginated mode the parent should also reset to the first page.
   */
  public readonly searchChange = output<string>();

  // --- Internal State ---

  /**
   * Whether component is in paginated mode
   * Determined by presence of paginationConfig
   */
  public readonly isPaginatedMode = computed(() => {
    const config = this.paginationConfig();
    return config !== null && config !== undefined;
  });

  /**
   * Effective items to display - switches based on mode
   * Paginated mode: uses loadedItems from parent
   * Static mode: uses items or flattened groups
   */
  public readonly effectiveItems = computed(() => {
    if (this.isPaginatedMode()) {
      return this.loadedItems();
    }

    // Static mode: use items or flattened groups
    const groups = this.groups();
    if (groups.length > 0) {
      return groups.flatMap(group => group.items);
    }
    return this.items();
  });

  /**
   * Computed flat list of all items (from either items or groups)
   * Used internally for consistency regardless of data structure
   * @deprecated Use effectiveItems instead
   */
  private readonly _allItems = computed(() => {
    const groups = this.groups();
    if (groups.length > 0) {
      return groups.flatMap(group => group.items);
    }
    return this.items();
  });

  /**
   * Whether component is using grouped data
   */
  public readonly hasGroups = computed(() => this.groups().length > 0);

  /**
   * Current search input value
   * Controlled via ControlValueAccessor
   */
  public searchValue = signal<string>('');

  /**
   * Whether the component is disabled
   * Controlled via ControlValueAccessor
   */
  public disabled = signal<boolean>(false);

  /**
   * Cached set of selected item IDs for O(1) lookup
   * Recomputes only when selectedItems changes
   * Used by isItemSelected to avoid O(n) operations per option
   */
  /**
   * Selected chips with their label and track key already resolved, so the template never
   * calls `displayFn()`/`trackByFn()` on every change detection cycle.
   */
  protected readonly selectedItemVms = computed(() => {
    const displayFn = this.displayFn();
    const trackByFn = this.trackByFn();
    return this.selectedItems().map((item) => ({ item, key: trackByFn(item), label: displayFn(item) }));
  });

  private readonly _selectedIdsSet = computed(() => {
    const trackBy = this.trackByFn();
    return new Set(this.selectedItems().map(trackBy));
  });

  // --- Pagination State ---

  /**
   * Current skip value for pagination (number of items already loaded)
   * Tracks pagination position for loadMore requests
   */
  private readonly _currentSkip = signal<number>(0);


  /**
   * Last search term used for pagination
   * Used to detect search changes and reset skip to 0
   */
  private readonly _lastSearchTerm = signal<string>('');

  /**
   * Whether search/filter functionality is enabled
   * Extracted from paginationConfig, defaults to false
   */
  private readonly _enableSearch = signal<boolean>(false);

  /**
   * Cached items for virtual scroll
   * Plain array property that gets updated when effectiveItems changes
   * CDK Virtual Scroll needs a plain array, not a signal
   */
  public virtualScrollItems: T[] = [];

  /** Pre-computed view-models for the virtual scroll viewport (updated alongside virtualScrollItems). */
  public virtualScrollOptionVms: OptionViewModel<T>[] = [];

  /** Pre-computed placeholder: empty string once any item is selected. */
  protected readonly currentPlaceholder = computed<string>(() =>
    this.selectedItems().length === 0 ? this.placeholder() : ''
  );

  /** Pre-computed option VMs for the flat (non-grouped, non-virtual) list. */
  protected readonly flatOptionVms = computed<OptionViewModel<T>[]>(() =>
    this.effectiveItems().map(item => this._toOptionVm(item))
  );

  /** Pre-computed group VMs for the grouped (non-virtual) list. */
  protected readonly groupOptionVms = computed<GroupOptionViewModel<T>[]>(() =>
    this.groups().map(group => ({
      label: group.label,
      itemVms: group.items.map(item => this._toOptionVm(item)),
    }))
  );

  // --- ViewChild ---

  /**
   * Reference to the chip input element
   * Used to clear input after selection and access autocomplete trigger
   */
  private readonly _chipInput = viewChild<ElementRef<HTMLInputElement>>('chipInput');

  /**
   * Reference to the virtual scroll viewport
   * Used to trigger recalculation when data changes
   */
  private readonly _virtualScrollViewport = viewChild(CdkVirtualScrollViewport);

  // --- Error Handling State ---

  private readonly _injector = inject(Injector);
  private readonly _cdr = inject(ChangeDetectorRef);
  private readonly _logger = inject(LoggerProvider).createScoped('MultiselectChipFieldComponent');
  public readonly ngControl = signal<NgControl | null>(null);


  // --- ControlValueAccessor Implementation ---

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private _onChange: (value: string) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private _onTouched: () => void = () => {};

  constructor() {
    // Defer NgControl lookup to avoid circular dependency
    // The NgControl directive will be available after the component is constructed
    queueMicrotask(() => {
      try {
        const ngControl = this._injector.get(NgControl, null, { optional: true, self: true }) || null;
        this.ngControl.set(ngControl);
      } catch {
        this.ngControl.set(null);
      }
    });

    // Watch for paginationConfig changes and update enableSearch
    effect(() => {
      const config = this.paginationConfig();
      this._enableSearch.set(config?.enableSearch === true);
    });

    // Watch for data changes and update virtual scroll items + their view-models
    effect(() => {
      const items = this.effectiveItems();

      // Update the array - must be plain array for CDK Virtual Scroll
      this.virtualScrollItems = [...items];
      this.virtualScrollOptionVms = items.map(item => this._toOptionVm(item));

      if (this.isPaginatedMode()) {
        // Update _currentSkip reactively based on actual loaded items
        // This ensures skip is only advanced when data actually arrives
        // Prevents permanent blocking if a loadMore request fails
        const loadedCount = this.loadedItems().length;
        this._currentSkip.set(loadedCount);

        // Trigger change detection to notify virtual scroll of data change
        this._cdr.detectChanges();

        // Force viewport to recalculate content size after data changes
        setTimeout(() => {
          const viewport = this._virtualScrollViewport();
          if (viewport) {
            viewport.checkViewportSize();
          }
        }, 0);
      }
    });
  }

  public writeValue(value: string): void {
    this.searchValue.set(value || '');
  }

  public registerOnChange(fn: (value: string) => void): void {
    this._onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  // --- Public Methods ---

  /**
   * Handles search input value changes.
   * Updates internal state, notifies parent via CVA, and emits
   * searchChange (debounced 300ms) in both static and paginated modes.
   */
  public onSearchChange(value: string): void {
    this.searchValue.set(value);
    this._onChange(value);
    this._handleSearchChange(value);
  }

  /**
   * Handles search changes for both static and paginated modes.
   * Emits searchChange output with debouncing (300ms).
   * In paginated mode also resets currentSkip to 0.
   */
  private _searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;

  private _handleSearchChange(searchTerm: string): void {
    if (this._searchDebounceTimer) {
      clearTimeout(this._searchDebounceTimer);
    }

    this._searchDebounceTimer = setTimeout(() => {
      const trimmedTerm = searchTerm.trim();

      if (this.isPaginatedMode()) {
        this._currentSkip.set(0);
        this._lastSearchTerm.set(trimmedTerm);
      }

      this.searchChange.emit(trimmedTerm);
    }, 300);
  }

  /**
   * Handles input blur event
   * Marks the control as touched for Angular forms validation
   */
  public onInputBlur(): void {
    // Simply call onTouched - no setTimeout needed!
    // The cm-field-errors component is subscribed to control.events
    // which emits TouchedChangeEvent synchronously when markAsTouched() is called
    this._onTouched();
  }

  /**
   * Handles item selection from autocomplete dropdown
   *
   * Behavior:
   * - If item is not selected: Emits `itemSelected` to add it
   * - If item is already selected: Emits `itemRemoved` to remove it (toggle-off)
   *
   * Note: The toggle-off path is typically unreachable if the parent correctly
   * filters selected items out of the `items` list. It exists as a safety mechanism
   * for edge cases where selected items might appear in the dropdown.
   *
   * Clears search input after action.
   *
   * @param event - The autocomplete selection event containing the selected item
   */
  public onOptionSelected(event: AutocompleteSelectionEvent<T> | null | undefined): void {
    // Defensive null checks
    if (!event || !event.option || event.option.value === undefined) {
      this._logger.warn('Invalid option selected event', { event });
      return;
    }

    const item = event.option.value;

    // Prevent selection of disabled items
    if (this.isItemDisabled(item)) {
      // Clear search input without emitting events
      this.searchValue.set('');
      this._onChange('');
      const input = this._chipInput();
      if (input) {
        input.nativeElement.value = '';
      }
      return;
    }

    // Toggle behavior: if already selected, remove it; otherwise add it
    // Note: If parent correctly filters items, the first branch is always taken
    if (this.isItemSelected(item)) {
      this.itemRemoved.emit(item);
    } else {
      this.itemSelected.emit(item);
    }

    // Reset NeoOption's internal `_selected` flag. In multiple mode the
    // underlying neo-option toggles this flag on every click, and the
    // autocomplete drops events where the flag is `false`. Items that stay
    // in the dropdown across clicks (e.g. a "Select all" sentinel) would
    // otherwise miss every other event.
    event.option.deselect?.();

    // Clear search input
    this.searchValue.set('');
    this._onChange('');
    const input = this._chipInput();
    if (input) {
      input.nativeElement.value = '';
    }
  }

  /**
   * Handles removal of a single chip
   * Emits the removed item to parent
   */
  public onChipRemoved(item: T): void {
    this.itemRemoved.emit(item);
  }

  /**
   * Handles removal of all chips
   * Emits event to clear all selections
   */
  public onAllChipsRemoved(): void {
    this.allItemsRemoved.emit();
  }

  /**
   * Handles action button click
   * Emits event to parent for business logic
   */
  public onButtonClicked(): void {
    this.buttonClick.emit();
  }

  /**
   * Handles autocomplete panel opened event
   * Called when dropdown panel is actually rendered in DOM
   */
  public onPanelOpened(): void {
    if (this.isPaginatedMode()) {
      // Reset virtual scroll viewport to top position
      // Fixes issue where dropdown shows blank after scrolling and reopening
      const viewport = this._virtualScrollViewport();
      if (viewport) {
        viewport.scrollToIndex(0);
        viewport.checkViewportSize();
      }
    }
  }

  /**
   * Handles autocomplete panel closed event
   */
  public onPanelClosed(): void {
    // No cleanup needed
  }

  /**
   * Checks if an item is currently selected
   * Used to show checkmarks in autocomplete dropdown
   *
   * Performance: O(1) lookup using cached Set, avoiding O(n) per option
   */
  public isItemSelected(item: T): boolean {
    const trackBy = this.trackByFn();
    const itemId = trackBy(item);
    return this._selectedIdsSet().has(itemId);
  }

  /**
   * Resolves an option's visual state. Uses `getOptionState` when provided;
   * otherwise falls back to the selectedItems-driven boolean.
   */
  public resolveOptionState(item: T): 'checked' | 'unchecked' | 'indeterminate' {
    const stateFn = this.getOptionState();
    if (stateFn) {
      return stateFn(item);
    }
    return this.isItemSelected(item) ? 'checked' : 'unchecked';
  }

  /**
   * Whether the option's pseudo-checkbox should appear filled.
   * Both `checked` and `indeterminate` use the filled visual; CSS swaps the
   * inner glyph for `indeterminate`.
   */
  public isOptionVisuallySelected(item: T): boolean {
    return this.resolveOptionState(item) !== 'unchecked';
  }

  /**
   * Whether the option should render the indeterminate (minus) glyph.
   */
  public isOptionIndeterminate(item: T): boolean {
    return this.resolveOptionState(item) === 'indeterminate';
  }

  /**
   * Checks if an item is disabled
   * Used to prevent selection of disabled items
   */
  public isItemDisabled(item: T): boolean {
    const disabledFn = this.disabledFn();
    return disabledFn ? disabledFn(item) : false;
  }

  /**
   * Gets the disabled reason for an item
   * Used for tooltip display
   */
  public getDisabledReason(item: T): string | null {
    const disabledReasonFn = this.disabledReasonFn();
    return disabledReasonFn ? disabledReasonFn(item) : null;
  }

  /**
   * Gets placeholder text based on selection state
   * Shows placeholder only when no items are selected
   */
  public getPlaceholder(): string {
    return this.selectedItems().length === 0 ? this.placeholder() : '';
  }

  /**
   * Calculates viewport height for virtual scrolling
   * CRITICAL: Must fit within Material Autocomplete's max-height constraint
   * @returns height in pixels
   */
  public calculateViewportHeight(): number {
    if (!this.isPaginatedMode()) {
      return 300; // Default height for non-paginated
    }

    const config = this.paginationConfig();
    if (!config) {
      return 300;
    }

    // Material Autocomplete's max-height constraint
    const materialMaxHeight = 238;

    // Calculate how many FULL items fit within Material's constraint
    const maxItemsFit = Math.floor(materialMaxHeight / config.itemHeight);

    // Ensure at least 1 item visible, max pageSize items
    const itemsToShow = Math.max(1, Math.min(maxItemsFit, config.pageSize));

    // Use exact multiple of itemHeight
    const calculatedHeight = itemsToShow * config.itemHeight;

    // Example: 238 / 42 = 5.666 → Math.floor = 5 items → 5 * 42 = 210px

    return calculatedHeight;
  }

  /**
   * TrackBy function for CDK virtual scroll
   * Adapts component's trackByFn to CDK's TrackByFunction signature
   * Arrow function to preserve `this` context
   * @param index - Item index
   * @param item - Item to track
   * @returns Unique identifier for item
   */
  public trackByForVirtualScroll = (index: number, item: T): string | number => {
    const trackBy = this.trackByFn();
    return trackBy(item);
  };

  /** TrackBy for the virtual scroll view-model list — uses the pre-computed key. */
  public readonly trackVmForVirtualScroll = (index: number, vm: OptionViewModel<T>): string | number => {
    return vm.trackKey;
  };

  private _toOptionVm(item: T): OptionViewModel<T> {
    return {
      item,
      trackKey: this.trackByFn()(item),
      displayText: this.displayFn()(item),
      subtext: this.subtextFn() !== null ? this.subtextFn()!(item) : null,
      isVisuallySelected: this.isOptionVisuallySelected(item),
      isIndeterminate: this.isOptionIndeterminate(item),
      isDisabled: this.isItemDisabled(item),
      disabledReason: this.getDisabledReason(item),
    };
  }

  /**
   * Cleanup on component destroy
   */
  public ngOnDestroy(): void {
    // Cleanup any timers
    if (this._searchDebounceTimer) {
      clearTimeout(this._searchDebounceTimer);
    }
  }

  /**
   * Handles infinite scroll for paginated mode
   * Called when user scrolls near bottom of dropdown
   * Emits loadMore with current pagination parameters
   *
   * @param scrolledIndex - Current scroll index from virtual scroller
   */
  public handleLoadMore(scrolledIndex: number): void {
    if (!this.isPaginatedMode()) {
      return;
    }

    const config = this.paginationConfig();
    if (!config) {
      return;
    }

    const loadedItems = this.loadedItems();
    const totalLoaded = loadedItems.length;

    // Check if we're within threshold (10 items from bottom)
    // Only trigger when user has actually scrolled near the end
    const threshold = 10;
    const isNearBottom = scrolledIndex >= totalLoaded - threshold;

    // Don't load if:
    // - Not near bottom
    // - Already loading
    // - No more items available
    // - Haven't loaded anything yet
    if (!isNearBottom || this.loadingMore() || !this.hasMore() || totalLoaded === 0) {
      return;
    }

    // Check if we've already requested more items
    // currentSkip > totalLoaded means we've requested but items haven't arrived yet
    const currentSkip = this._currentSkip();
    if (currentSkip > totalLoaded) {
      return;
    }

    // Calculate next page parameters
    const nextSkip = totalLoaded;
    const take = config.pageSize;
    const search = this._lastSearchTerm();

    // Note: _currentSkip is updated reactively in the items effect
    // based on actual loadedItems.length, not optimistically here.
    // This ensures retry works if the request fails.

    // Emit loadMore event
    this.loadMore.emit({
      skip: nextSkip,
      take: take,
      search: search,
      enableSearch: this._enableSearch(),
    });
  }
}
